import { defineStore } from "pinia";
import {
    Cart,
    KeySellingPoint,
    LineItemModel,
} from "../../../utils/API/Cart/cartAPISchema";
import { formatCurrency } from "../../../utils/currencyHelpers";
import { formatWithoutRounding } from "../../../mixins/productPrice.mixin";
import { useCheckoutStateStore } from "../../checkout/stores/checkoutStateStore";
import { CheckoutSteps } from "../../../services/models/checkout.models";
import CheckoutHelpers from "../../../../../helpers/checkoutHelpers";
import { set } from "zod";

interface PaymentLogos {
    id: string;
    url: string;
    alt: string;
};

export const useShoppingCartStore = defineStore("shoppingCart", {
    state: () => ({
        cart: null as Cart | null | undefined,
        ksps: null as KeySellingPoint[] | null | undefined,
        discounts: [] as string[] | null | undefined,
        lineItemsOutOfStock: [] as LineItemModel[],
        lineItemErrors: {} as Record<string, string> | null | undefined,
        summaryError: "" as string | null | undefined,
        discountError: "" as string | null | undefined,
        paymentLogos: [] as PaymentLogos[],
    }),
    actions: {
        setCart(cart: Cart | null | undefined) {
            this.cart = cart;
        },
        setKsps(ksps: KeySellingPoint[] | null | undefined) {
            this.ksps = ksps;
        },
        addDiscountCode(discount: string) {
            if (!this.discounts) return;
            this.discounts.push(discount);
            this.discounts = [...this.discounts];
        },
        removeDiscountCode(discount: string) {
            if (!this.discounts) return;
            this.discounts = this.discounts.filter((d) => d !== discount);
        },
        setLineItemsOutOfStock(lineItems: LineItemModel[]) {
            this.lineItemsOutOfStock = lineItems;
        },
        setLineItemError(lineItemEan: string, error: string) {
            this.lineItemErrors = {
                ...this.lineItemErrors,
                [lineItemEan]: error,
            };
        },
        setLineItemErrors(errors: Record<string, string> | null | undefined) {
            this.lineItemErrors = errors;
        },
        setSummaryError(error: string | null | undefined) {
            this.summaryError = error;
        },
        setDiscountError(error: string | null | undefined) {
            this.discountError = error;
        },
        setPaymentLogos(paymentLogos: PaymentLogos[]) {
            this.paymentLogos = paymentLogos;
        },
        clear() {
            this.cart = null;
            this.ksps = null;
            this.discounts = [];
            this.lineItemsOutOfStock = [];
            this.lineItemErrors = {};
            this.summaryError = null;
            this.discountError = null;
            this.paymentLogos = [];
        }
    },
    getters: {
        shippingCostFormatted: (state): string => {
            const checkoutState = useCheckoutStateStore();
            if (!state.cart) return "Beregnes senere";

            if (
                (state.cart?.freeShippingActive &&
                    state.cart?.freeShippingLimit.amount <=
                        state.cart?.subTotal.amount) ||
                !state.cart?.selectedShippingMethodPrice.amount
            )
                return "Fri frakt";
            if (
                state.cart?.selectedShippingMethodPrice.amount &&
                (checkoutState.currentCheckoutStep === CheckoutSteps.Delivery ||
                    checkoutState.currentCheckoutStep === CheckoutSteps.Payment)
            ) {
                return `${formatCurrency(state.cart?.selectedShippingMethodPrice.currency)} ${formatWithoutRounding(state.cart?.selectedShippingMethodPrice.amount)}`;
            }
            return "Beregnes senere";
        },
        totalAmount: (state): number => {
            const checkoutState = useCheckoutStateStore();
            if (
                checkoutState.currentCheckoutStep === CheckoutSteps.Delivery ||
                checkoutState.currentCheckoutStep === CheckoutSteps.Payment
            ) {
                return state.cart?.total.amount || 0;
            }
            return state.cart?.subTotal.amount || 0;
        },
        esalesKeys: (state) => {
            return state.cart?.lineItems.map((li) => li.esalesKey) ?? [];
        },
        lineItemsInStock: (state) => {
            return state.cart?.lineItems.filter(
                (lineItem) => lineItem.quantity <= lineItem.currentInventory,
            );
        },
        freeShipping: (state) => {
            if (!state.cart) return false;

            return CheckoutHelpers.qualifiesForFreeShipping(
                state.cart.freeShippingActive,
                state.cart.freeShippingLimit,
                state.cart.subTotal,
            );
        },
        shippingCallToActionText: (state) => {
            const remainingAmount =
                CheckoutHelpers.amountNeededToQualifyForFreeShipping(
                    state.cart?.freeShippingActive,
                    state.cart?.freeShippingLimit,
                    state.cart?.subTotal,
                )?.amount;

            if (!remainingAmount) return "";

            return `Du er ${formatWithoutRounding(remainingAmount)} unna fri frakt`;
        },
        currentCartSize(): number {
            return (
                this.lineItemsInStock?.reduce(
                    (acc, lineItem) => acc + lineItem.quantity,
                    0,
                ) || 0
            );
        },
    },
});
